import { z } from "zod";
import { SharedApiError, normalizeApiBaseUrl, type FetchLike } from "./shared";

export const messageItemSchema = z.object({
  id: z.string().min(1),
  content: z.string(),
  user_id: z.string().min(1),
  user_name: z.string().optional(),
  created_at: z.number(),
  thread_id: z.string().optional(),
  reply_count: z.number().int(),
  is_edited: z.boolean(),
});

export const listMessagesSuccessSchema = z.object({
  messages: z.array(messageItemSchema),
  has_more: z.boolean(),
});

export type MessageItem = z.infer<typeof messageItemSchema>;
export type ListMessagesSuccess = z.infer<typeof listMessagesSuccessSchema>;

export function listMessagesPath(
  channelId: string,
  options?: { limit?: number; before?: string },
): string {
  const params = new URLSearchParams();
  if (options?.limit) params.set("limit", String(options.limit));
  if (options?.before) params.set("before", options.before);
  const qs = params.toString();
  return `/api/channels/${encodeURIComponent(channelId)}/messages${qs ? `?${qs}` : ""}`;
}

export async function listMessagesRequest({
  apiBaseUrl,
  accessToken,
  channelId,
  limit,
  before,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  channelId: string;
  limit?: number;
  before?: string;
  fetchImpl?: FetchLike;
}): Promise<ListMessagesSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${listMessagesPath(channelId, { limit, before })}`,
    {
      method: "GET",
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const msg =
      payload && typeof payload === "object" && "error" in payload
        ? (payload as { error: string }).error
        : "Failed to list messages";
    throw new SharedApiError(msg, response.status);
  }

  const payload = await response.json().catch(() => null);
  const parsed = listMessagesSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid list messages response", 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}
